import React, { useState } from 'react';
import { uploadFile, deleteFile } from '../../services/db';

interface ImageUploaderProps {
  currentImageUrl?: string;
  onImageUploaded: (url: string) => void;
  onImageDeleted: () => void;
  folderPath: string; // e.g. "homepage/hero", "services/cards"
  label?: string;
}

export default function ImageUploader({ currentImageUrl, onImageUploaded, onImageDeleted, folderPath, label = "Görsel Yükle" }: ImageUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Dosya boyutu 5MB\'dan küçük olmalıdır.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // If there's an existing image, we theoretically could delete it to save space, 
      // but let's just upload the new one and overwrite the reference.
      const downloadUrl = await uploadFile(file, folderPath);
      onImageUploaded(downloadUrl);
    } catch (err) {
      console.error(err);
      setError('Görsel yüklenirken bir hata oluştu.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentImageUrl) return;
    try {
      await deleteFile(currentImageUrl);
      onImageDeleted();
    } catch (err) {
      console.error(err);
      setError('Görsel silinemedi.');
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-zinc-400 text-xs font-bold uppercase tracking-wider">{label}</label>
      
      {error && <p className="text-red-500 text-xs font-bold">{error}</p>}

      {currentImageUrl ? (
        <div className="relative group rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950 aspect-video md:aspect-[21/9]">
          <img src={currentImageUrl} alt="Uploaded API Asset" className="w-full h-full object-cover opacity-80 group-hover:opacity-40 transition-opacity" />
          
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity gap-4">
             <label className="cursor-pointer bg-white text-black font-bold px-4 py-2 rounded-lg hover:bg-zinc-200 transition-colors flex items-center gap-2">
                <i className="ri-edit-line"></i> Değiştir
                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} disabled={uploading} />
             </label>
             <button onClick={handleDelete} className="bg-red-600 text-white font-bold px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2">
                <i className="ri-delete-bin-line"></i> Sil
             </button>
          </div>
        </div>
      ) : (
        <div className="relative border-2 border-dashed border-zinc-800 rounded-xl p-8 text-center hover:bg-zinc-900 transition-colors">
          <input 
            type="file" 
            accept="image/*" 
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
            onChange={handleFileChange}
            disabled={uploading}
          />
          {uploading ? (
            <div className="text-zinc-400 font-bold flex flex-col items-center gap-2">
              <i className="ri-loader-4-line text-2xl animate-spin text-red-500"></i>
              Yükleniyor...
            </div>
          ) : (
            <div className="text-zinc-500 flex flex-col items-center gap-2">
              <i className="ri-image-add-line text-3xl mb-1"></i>
              <span className="font-bold text-sm">Görsel Seç veya Sürükle</span>
              <span className="text-xs">PNG, JPG, WEBP (Max: 5MB)</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
